import { createRng, hashSeed } from './rng.js';
import { parseTime } from './clock.js';
import {
  BLOCKS,
  CLIENTS,
  EFFORT,
  EVENT_TYPES,
  OVERSUBSCRIBE,
  SHORTFALL_COUNT,
} from '../data/beoTemplates.js';
import { SHORTFALLS, SHORTFALL_IDS, STOCK_BUFFER, estimateFor } from '../data/resources.js';
import { CART_BUDGET, CART_MAX, stackLimit } from './mass.js';

/**
 * Builds a day's board.
 *
 * Two jobs, and they are the same job: decide what is posted, and make sure
 * there is more of it than there are hours. A board you can clear is a
 * checklist; a board you cannot is a set of decisions about which department
 * you are prepared to disappoint. So the generator keeps adding events until
 * the total estimated work is comfortably past the shift, and stops before it
 * becomes absurd.
 *
 * Every roll comes off a seeded stream keyed on the run seed and the day, so a
 * reload gives back the same board. A save that restores the day but re-rolls
 * its events is a save that changes the game underneath the player.
 */

/**
 * Game minutes of work an event represents.
 *
 * Trips dominate — the property is two hundred and fifty feet long and the cart
 * holds four rounds — so the model is trips plus a small per-item cost, with
 * the shortfalls' own estimates added on. Calibrated against a walked run; see
 * EFFORT in the templates.
 */
export function estimateMinutes(beo, { rungFor = () => 'cold' } = {}) {
  const { tableCount, chairsPerTable } = beo.setup;
  const chairs = tableCount * chairsPerTable;
  const onCart = (kind) => stackLimit(kind, { budget: CART_BUDGET, cap: CART_MAX });
  const trips =
    Math.ceil(tableCount / onCart('round')) +
    Math.ceil(chairs / onCart('chair')) +
    Math.ceil(tableCount / onCart('linen'));
  const items = tableCount * 2 + chairs;
  /*
   * And what being short of things costs, which depends on who is holding them
   * and what they make of you.
   *
   * A shortfall is a negotiation plus a walk, and the rung moves the walk: the
   * same linen is eighty feet away, thirty feet away, or already on the shelf.
   * A board that budgets the eighty every time is a board that hands a player
   * everybody likes the same day as one nobody does, and the half of the
   * property they have earned goes unspent.
   *
   * `rungFor` takes a department and returns a rung. It defaults to cold so the
   * module stays pure and testable without a standing system in the room.
   */
  const shortfalls = (beo.shortfalls ?? []).reduce(
    (total, entry) =>
      total + estimateFor(SHORTFALLS[entry.id], rungFor(SHORTFALLS[entry.id]?.owner)),
    0
  );
  return Math.round(trips * EFFORT.tripMinutes + items * EFFORT.itemMinutes + shortfalls);
}

/** Every anchor a layout covers, as `col,row` keys. */
function anchorsOf(layout) {
  const keys = [];
  for (let c = layout.col; c < layout.col + layout.cols; c += 1) {
    for (let r = layout.row; r < layout.row + layout.rows; r += 1) keys.push([c, r]);
  }
  return keys;
}

/** Whether a block placed at an offset keeps every round off the dance floor. */
function layoutIsClear(layout, grid) {
  const [kx0, kz0, kx1, kz1] = grid.keepClear;
  return anchorsOf(layout).every(([c, r]) => {
    const x = grid.origin[0] + c * grid.spacing;
    const z = grid.origin[1] + r * grid.spacing;
    return !(x >= kx0 && x <= kx1 && z >= kz0 && z <= kz1);
  });
}

/**
 * Places a block somewhere legal on the grid.
 *
 * Every offset is enumerated and then one is picked, rather than rolling
 * offsets until one works. A block that only fits in two places would otherwise
 * make the generator spin, and the failure would show up as a hang on some
 * future day nobody has played yet.
 */
function placeBlock(block, grid, rng) {
  const spots = [];
  for (let col = 0; col + block.cols <= grid.cols; col += 1) {
    for (let row = 0; row + block.rows <= grid.rows; row += 1) {
      const layout = { col, row, cols: block.cols, rows: block.rows };
      if (layoutIsClear(layout, grid)) spots.push(layout);
    }
  }
  return spots.length ? rng.pick(spots) : null;
}

/** One to three distinct shortfalls, weighted, with their amounts rolled. */
function rollShortfalls(rng, setup) {
  const count = rng.int(SHORTFALL_COUNT[0], SHORTFALL_COUNT[1]);
  const pool = SHORTFALL_IDS.map((id) => SHORTFALLS[id]);
  const chosen = [];

  while (chosen.length < count && chosen.length < pool.length) {
    const left = pool.filter((entry) => !chosen.some((taken) => taken.id === entry.id));
    if (!left.length) break;
    const picked = rng.weighted(left);
    const entry = { id: picked.id, resolved: false, via: null };

    if (picked.kind === 'goods') {
      const [low, high] = picked.amount;
      // Never withhold more than the event needs; being short of nine linens
      // for a six-round event reads as a bug rather than as a shortfall.
      const need =
        picked.item === 'chair'
          ? setup.tableCount * setup.chairsPerTable
          : setup.tableCount;
      entry.amount = Math.max(1, Math.min(rng.int(low, high), Math.max(1, need - 1)));
    }
    chosen.push(entry);
  }
  return chosen;
}

/** What banquet storage holds for an event, after the shortfalls are taken out. */
export function stockFor(beo) {
  const { tableCount, chairsPerTable } = beo.setup;
  const stock = {
    round: tableCount + STOCK_BUFFER.round,
    chair: tableCount * chairsPerTable + STOCK_BUFFER.chair,
    linen: tableCount + STOCK_BUFFER.linen,
  };
  for (const entry of beo.shortfalls ?? []) {
    const shortfall = SHORTFALLS[entry.id];
    if (shortfall?.kind !== 'goods' || entry.resolved) continue;
    stock[shortfall.item] = Math.max(0, stock[shortfall.item] - entry.amount);
  }
  return stock;
}

/**
 * The day's events.
 *
 * Deadlines are spread across the shift rather than clustered, because a board
 * where everything is due at once is a board with one decision on it. The first
 * event is due about ninety minutes in, the last about half an hour before the
 * end, and the rest are laid out between.
 */
/**
 * How much more often a room belongs to one of the departments the story is
 * about. In the second act the Kitchen, Engineering and Valet are what you are
 * there to win over, and at their own weights (two, one and one in seventeen)
 * a week could go by without a room of theirs on the board.
 */
export const FAVOURED = 4;

/**
 * How long a room takes somebody playing normally, as a multiple of the
 * board's estimate, and the slack on top of it.
 *
 * The estimate is a skilled run with the cart (`EFFORT`, the bench's pace). A
 * new player on foot, reading the screen, takes far longer: measured off
 * `fullgame.mjs`, the scripted player that plays a new game on a pad from what
 * is on the screen, its first six rooms took 1.55 to 3.24 times the estimate
 * in real time, 1.9 in the middle, and small rooms ran slower than large (the
 * walk to the board and the storeroom is the same for both). A deadline is
 * sized at 2.5 times, plus `DEADLINE_SLACK`, so the player it was measured on
 * makes it with time over on all but its worst room, and a skilled one has
 * the afternoon. The card's estimate still says the skilled number, and its
 * "at your pace" line says yours.
 */
export const NORMAL_PACE = 2.5;
export const DEADLINE_SLACK = 30;

/** Game minutes from taking a room to its deadline, at a normal pace with slack. */
export function normalMinutes(estimate) {
  return Math.round(EFFORT.overheadMinutes + (estimate ?? 0) * NORMAL_PACE + DEADLINE_SLACK);
}

export function generateDay({ day, shift, grid, seed = 'lion', rungFor = () => 'cold', favour = [] }) {
  const rng = createRng(hashSeed(seed, 'board', day));
  const start = parseTime(shift.start);
  let end = parseTime(shift.end);
  if (end <= start) end += 24 * 60;

  const budget = end - start;
  // The first deadline is two and a half hours in, not ninety minutes. Ninety
  // put the earliest event's deadline before the work fitted into it, so the
  // top card on every board was one nobody could ever take.
  const first = start + 150;
  const last = end - 30;

  const beos = [];
  let posted = 0;
  // A hard cap as well as a budget: a generator that cannot reach its target
  // because every event it rolls is tiny must still terminate.
  for (let index = 0; index < 12; index += 1) {
    if (posted >= budget * OVERSUBSCRIBE.min && index >= 4) break;

    const type = favour.length
      ? rng.weighted(EVENT_TYPES, (entry) => (entry.weight ?? 1) * (favour.includes(entry.owner) ? FAVOURED : 1))
      : rng.weighted(EVENT_TYPES);
    const block = rng.weighted(BLOCKS);
    const layout = placeBlock(block, grid, rng);
    if (!layout) continue;

    const tableCount = layout.cols * layout.rows;
    const chairsPerTable = rng.next() < 0.75 ? 8 : 10;
    const setup = {
      tableType: 'round',
      tableCount,
      chairsPerTable,
      linen: rng.pick(type.linens),
      layout,
      staging: type.staging,
    };

    const beo = {
      id: `beo-${day}-${index}`,
      code: `BEO ${1100 + day * 10 + index}`,
      client: rng.pick(CLIENTS),
      owner: type.owner,
      type: type.id,
      room: 'ballroom',
      day,
      covers: tableCount * chairsPerTable,
      setup,
      note: rng.pick(type.notes),
      shortfalls: rollShortfalls(rng, setup),
    };
    beo.title = `${beo.client} ${type.name}`;

    // Award scales with the size of the room and who is watching.
    const base = 6 + tableCount * 2;
    beo.award = { banquets: base };
    if (type.owner !== 'banquets') beo.award[type.owner] = Math.round(base * 0.6);

    beo.estimate = estimateMinutes(beo, { rungFor });
    posted += beo.estimate;
    beos.push(beo);
    if (posted >= budget * OVERSUBSCRIBE.max) break;
  }

  /*
   * Deadlines last, once the count is known.
   *
   * Sorted small-first, which is both what a real day looks like — the Rotary
   * lunch is due before the gala — and the only way the earliest card on the
   * board is ever takeable. Spread evenly, but never earlier than a normal
   * player could set it from the start of the shift (`normalMinutes`), because
   * a deadline that arrives before the job can be done by the person playing
   * is not a hard choice, it is a write-up waiting to happen.
   */
  beos.sort((a, b) => a.estimate - b.estimate);

  const gap = beos.length > 1 ? (last - first) / (beos.length - 1) : 0;
  let previous = 0;
  beos.forEach((beo, index) => {
    const spread = first + gap * index;
    const earliestPossible = start + normalMinutes(beo.estimate);
    const setupBy = Math.min(
      Math.max(Math.round(Math.max(spread, earliestPossible)), previous + 20),
      end - 15
    );
    previous = setupBy;
    beo.setupBy = clockText(setupBy);
    beo.doors = clockText(setupBy + 30);
    // Posted before the shift opens: you can read tomorrow's board tonight.
    beo.postedAt = clockText(Math.max(0, start - 120));
  });

  return beos;
}

function clockText(minutes) {
  const wrapped = ((Math.round(minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(wrapped / 60)).padStart(2, '0')}:${String(wrapped % 60).padStart(2, '0')}`;
}

/** Total posted work against the shift's own length, for the board's header. */
export function boardLoad(beos, shift) {
  const start = parseTime(shift.start);
  let end = parseTime(shift.end);
  if (end <= start) end += 24 * 60;
  const work = beos.reduce((total, beo) => total + (beo.estimate ?? estimateMinutes(beo)), 0);
  return { work, shiftMinutes: end - start, ratio: work / (end - start) };
}
